/*
 * Copyright © 2024-2026 symecode.com. All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution is prohibited.
 */


import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiClient } from '../../services/api';
import BackButton from '../common/BackButton';
import './AdminCompanyForm.css';

const AdminCompanyForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const isEditMode = !!id;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    contactNotes: '',
    bidFrontPage: '',
    bidLastPage: '',
    generateBidCost: '0',
    updateBidCost: '0',
    credits: '0'
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initialLoading, setInitialLoading] = useState(isEditMode);

  useEffect(() => {
    if (isEditMode && id) {
      loadCompany(parseInt(id));
    }
  }, [id, isEditMode]);

  const loadCompany = async (companyId: number) => {
    try {
      setInitialLoading(true);
      setError(null);

      const response = await apiClient.getCompanyById(companyId);

      if (response.success && response.company) {
        setFormData({
          name: response.company.name || '',
          email: response.company.email || '',
          phoneNumber: response.company.phoneNumber || '',
          contactNotes: response.company.contactNotes || '',
          bidFrontPage: response.company.bidFrontPage || '',
          bidLastPage: response.company.bidLastPage || '',
          generateBidCost: ((response.company.generateBidCost || 0) / 100).toFixed(2),
          updateBidCost: ((response.company.updateBidCost || 0) / 100).toFixed(2),
          credits: ((response.company.credits || 0) / 100).toFixed(2)
        });
      } else {
        setError(response.error || 'Failed to load company');
      }
    } catch (err: any) {
      console.error('Error loading company:', err);
      setError(err.message || 'An error occurred while loading the company');
    } finally {
      setInitialLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleMoneyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // Allow only numbers and decimal point
    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setError('Company name is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Convert dollar amounts to cents
      const generateBidCostCents = Math.round(parseFloat(formData.generateBidCost || '0') * 100);
      const updateBidCostCents = Math.round(parseFloat(formData.updateBidCost || '0') * 100);
      const creditsCents = Math.round(parseFloat(formData.credits || '0') * 100);

      const companyData = {
        name: formData.name,
        email: formData.email || undefined,
        phoneNumber: formData.phoneNumber || undefined,
        contactNotes: formData.contactNotes || undefined,
        bidFrontPage: formData.bidFrontPage || undefined,
        bidLastPage: formData.bidLastPage || undefined,
        generateBidCost: generateBidCostCents,
        updateBidCost: updateBidCostCents,
        credits: creditsCents
      };

      let response;
      if (isEditMode && id) {
        response = await apiClient.updateCompany(parseInt(id), companyData);
      } else {
        response = await apiClient.createCompany(companyData);
      }

      if (response.success) {
        navigate('/companies');
      } else {
        setError(response.error || `Failed to ${isEditMode ? 'update' : 'create'} company`);
      }
    } catch (err: any) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} company:`, err);
      setError(err.message || `An error occurred while ${isEditMode ? 'updating' : 'creating'} the company`);
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="admin-company-form-container">
        <div className="loading">Loading company...</div>
      </div>
    );
  }

  return (
    <div className="admin-company-form-container">
      <BackButton to="/companies" />

      <div className="form-header">
        <h2>{isEditMode ? 'Edit Company' : 'Create New Company'}</h2>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="admin-company-form">
        <div className="form-section">
          <h3>Company Information</h3>

          <div className="form-group">
            <label htmlFor="name">
              Company Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="Enter company name"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="contact@company.com"
              />
            </div>

            <div className="form-group">
              <label htmlFor="phoneNumber">Phone Number</label>
              <input
                type="tel"
                id="phoneNumber"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
                placeholder="555-0100"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="contactNotes">Contact Notes</label>
            <textarea
              id="contactNotes"
              name="contactNotes"
              value={formData.contactNotes}
              onChange={handleChange}
              rows={3}
              placeholder="Additional contact information or notes"
            />
          </div>
        </div>

        <div className="form-section">
          <h3>Pricing Configuration</h3>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="generateBidCost">Generate Bid Cost (USD)</label>
              <div className="dollar-input-wrapper">
                <span className="dollar-sign">$</span>
                <input
                  type="text"
                  id="generateBidCost"
                  name="generateBidCost"
                  value={formData.generateBidCost}
                  onChange={handleMoneyChange}
                  placeholder="0.00"
                  className="dollar-input"
                />
              </div>
              <small className="input-hint">Cost to generate a new bid package</small>
            </div>

            <div className="form-group">
              <label htmlFor="updateBidCost">Update Bid Cost (USD)</label>
              <div className="dollar-input-wrapper">
                <span className="dollar-sign">$</span>
                <input
                  type="text"
                  id="updateBidCost"
                  name="updateBidCost"
                  value={formData.updateBidCost}
                  onChange={handleMoneyChange}
                  placeholder="0.00"
                  className="dollar-input"
                />
              </div>
              <small className="input-hint">Cost to update an existing bid package</small>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="credits">Initial Credits (USD)</label>
            <div className="dollar-input-wrapper" style={{ maxWidth: '300px' }}>
              <span className="dollar-sign">$</span>
              <input
                type="text"
                id="credits"
                name="credits"
                value={formData.credits}
                onChange={handleMoneyChange}
                placeholder="0.00"
                className="dollar-input"
              />
            </div>
            <small className="input-hint">
              {isEditMode ? 'Current credit balance' : 'Starting credit balance for new company'}
            </small>
          </div>
        </div>

        <div className="form-section">
          <h3>Bid Templates (Optional)</h3>

          <div className="form-group">
            <label htmlFor="bidFrontPage">Bid Front Page Content</label>
            <textarea
              id="bidFrontPage"
              name="bidFrontPage"
              value={formData.bidFrontPage}
              onChange={handleChange}
              rows={5}
              placeholder="HTML content for the front page of bid packages"
            />
            <small className="input-hint">This content will appear on the front page of generated bid packages</small>
          </div>

          <div className="form-group">
            <label htmlFor="bidLastPage">Bid Last Page Content</label>
            <textarea
              id="bidLastPage"
              name="bidLastPage"
              value={formData.bidLastPage}
              onChange={handleChange}
              rows={5}
              placeholder="HTML content for the last page of bid packages"
            />
            <small className="input-hint">This content will appear on the last page of generated bid packages</small>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="cancel-button"
            onClick={() => navigate('/companies')}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="submit-button"
            disabled={loading}
          >
            {loading ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Company' : 'Create Company')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminCompanyForm;
